import { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
  ClipboardDocumentListIcon,
  PlusIcon,
  CheckIcon,
  XMarkIcon,
  KeyIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentCheckIcon,
} from '@heroicons/react/24/outline';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import {
  fetchKeyRequests,
  createKeyRequestAsync,
  approveKeyRequestAsync,
  rejectKeyRequestAsync,
  fulfillKeyRequestAsync,
  cancelKeyRequestAsync,
} from '../store/keyRequestsSlice';
import { fetchPeople, createPersonAsync } from '../store/peopleSlice';
import { fetchInventory } from '../store/keyInventorySlice';
import { fetchKeyTypes } from '../store/keyTypesSlice';
import { KeyRequest, Person, KeyInventoryItem, KeyType, Location, User } from '../types';
import Card from '../components/Card';
import Button from '../components/Button';
import Modal from '../components/Modal';
import PersonSearch from '../components/PersonSearch';

interface PersonSearchResult {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  department?: string;
  source: 'azuread' | 'cosmosdb';
  azureAdId?: string;
}

interface RequestFormData {
  personId: string;
  keyTypeId: string;
  quantity: number;
  notes: string;
}

type StatusFilter = 'all' | 'pending' | 'approved' | 'rejected' | 'fulfilled';

export default function KeyRequests() {
  const dispatch = useAppDispatch();
  const { requests, loading } = useAppSelector((state) => state.keyRequests);
  const { people } = useAppSelector((state) => state.people);
  const locations = useAppSelector((state) => state.locations.locations);
  const inventory = useAppSelector((state) => state.keyInventory.inventory);
  const keyTypes = useAppSelector((state) => state.keyTypes.keyTypes);
  const currentUser = useAppSelector((state) => state.users.currentUser);
  const users = useAppSelector((state) => state.users.users);

  const isAdmin = currentUser?.role === 'admin';
  const isSiteAdmin = currentUser?.role === 'site-admin';
  const isKeyRequester = currentUser?.role === 'key-requester';
  const canApprove = isAdmin || isSiteAdmin;

  // Modal states
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [isFulfillModalOpen, setIsFulfillModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<KeyRequest | null>(null);
  const [reviewAction, setReviewAction] = useState<'approve' | 'reject'>('approve');
  const [reviewNotes, setReviewNotes] = useState('');
  const [fulfillNotes, setFulfillNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Filter states
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Request form - using full PersonSearch result to track source
  const [selectedPerson, setSelectedPerson] = useState<PersonSearchResult | null>(null);
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const requestForm = useForm<RequestFormData>({
    defaultValues: { quantity: 1 },
  });

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch data on mount
  useEffect(() => {
    dispatch(fetchKeyRequests());
    dispatch(fetchPeople());
    dispatch(fetchInventory());
    dispatch(fetchKeyTypes());
  }, [dispatch]);

  // Filter requests
  const filteredRequests = useMemo(() => {
    let result = requests;

    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter(r => r.status === statusFilter);
    }

    // Search filter
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      result = result.filter(r => {
        const person = people.find((p: Person) => p.id === r.personId);
        const requester = users.find((u: User) => u.id === r.requestedBy);
        return (
          person?.name.toLowerCase().includes(searchLower) ||
          requester?.name.toLowerCase().includes(searchLower) ||
          r.notes?.toLowerCase().includes(searchLower)
        );
      });
    }

    return result;
  }, [requests, statusFilter, debouncedSearch, people, users]);

  // Helper functions
  const getPerson = (personId: string) => people.find((p: Person) => p.id === personId);
  const getUser = (userId: string) => users.find((u: User) => u.id === userId);
  const getKeyType = (keyTypeId: string) => keyTypes.find((kt: KeyType) => kt.id === keyTypeId);
  const getLocation = (locationId: string) => locations.find((l: Location) => l.id === locationId);
  const getInventoryItem = (itemId: string) => inventory.find((i: KeyInventoryItem) => i.id === itemId);

  const getRequestDetails = (request: KeyRequest) => {
    const invItem = getInventoryItem(request.keyInventoryItemId);
    const keyType = invItem ? getKeyType(invItem.keyTypeId) : null;
    const location = invItem ? getLocation(invItem.locationId) : null;
    const person = getPerson(request.personId);
    const requester = getUser(request.requestedBy);
    const reviewer = request.reviewedBy ? getUser(request.reviewedBy) : null;
    return { invItem, keyType, location, person, requester, reviewer };
  };

  const getStatusBadge = (status: KeyRequest['status']) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
      approved: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      rejected: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      fulfilled: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    };
    const icons = {
      pending: <ClockIcon className="w-3.5 h-3.5" />,
      approved: <CheckIcon className="w-3.5 h-3.5" />,
      rejected: <XMarkIcon className="w-3.5 h-3.5" />,
      fulfilled: <DocumentCheckIcon className="w-3.5 h-3.5" />,
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${styles[status]}`}>
        {icons[status]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Modal handlers
  const handleOpenRequestModal = () => {
    setSelectedPerson(null);
    setSelectedLocationId('');
    requestForm.reset({ quantity: 1 });
    setIsRequestModalOpen(true);
  };

  const handleCloseRequestModal = () => {
    setIsRequestModalOpen(false);
    setSelectedPerson(null);
    setSelectedLocationId('');
    requestForm.reset();
  };

  const handleOpenReviewModal = (request: KeyRequest, action: 'approve' | 'reject') => {
    setSelectedRequest(request);
    setReviewAction(action);
    setReviewNotes('');
    setIsReviewModalOpen(true);
  };

  const handleCloseReviewModal = () => {
    setIsReviewModalOpen(false);
    setSelectedRequest(null);
    setReviewNotes('');
  };

  const handleOpenFulfillModal = (request: KeyRequest) => {
    setSelectedRequest(request);
    setFulfillNotes('');
    setIsFulfillModalOpen(true);
  };

  const handleCloseFulfillModal = () => {
    setIsFulfillModalOpen(false);
    setSelectedRequest(null);
    setFulfillNotes('');
  };

  // Form submissions
  const onSubmitRequest = async (data: RequestFormData) => {
    if (!selectedPerson || !currentUser || !selectedLocationId) return;

    setIsSaving(true);
    try {
      let personId = selectedPerson.id;

      // If person is from Azure AD and not yet in the system, create them first
      if (selectedPerson.source === 'azuread') {
        const now = new Date().toISOString();
        const newPerson: Person = {
          id: `person-${Date.now()}`,
          name: selectedPerson.name,
          email: selectedPerson.email,
          phone: selectedPerson.phone,
          department: selectedPerson.department,
          azureAdId: selectedPerson.azureAdId || selectedPerson.id,
          status: 'active',
          createdAt: now,
          updatedAt: now,
        };

        const createdPerson = await dispatch(createPersonAsync(newPerson)).unwrap();
        personId = createdPerson.id;
        // Refresh people list
        dispatch(fetchPeople());
      }

      await dispatch(createKeyRequestAsync({
        keyTypeId: data.keyTypeId,
        locationId: selectedLocationId,
        personId,
        quantity: data.quantity,
        notes: data.notes,
      })).unwrap();
      handleCloseRequestModal();
      dispatch(fetchKeyRequests());
    } catch (error) {
      console.error('Failed to create request:', error);
      alert('Failed to create request. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReview = async () => {
    if (!selectedRequest) return;

    setIsSaving(true);
    try {
      if (reviewAction === 'approve') {
        await dispatch(approveKeyRequestAsync({
          id: selectedRequest.id,
          reviewNotes,
        })).unwrap();
      } else {
        await dispatch(rejectKeyRequestAsync({
          id: selectedRequest.id,
          reviewNotes,
        })).unwrap();
      }
      handleCloseReviewModal();
      dispatch(fetchKeyRequests());
    } catch (error) {
      console.error('Failed to review request:', error);
      alert('Failed to process review. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleFulfill = async () => {
    if (!selectedRequest) return;

    setIsSaving(true);
    try {
      await dispatch(fulfillKeyRequestAsync({
        id: selectedRequest.id,
        notes: fulfillNotes,
      })).unwrap();
      handleCloseFulfillModal();
      dispatch(fetchKeyRequests());
      dispatch(fetchInventory()); // Refresh inventory since it changed
    } catch (error) {
      console.error('Failed to fulfill request:', error);
      alert(`Failed to fulfill request: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelRequest = async (request: KeyRequest) => {
    if (!confirm('Are you sure you want to cancel this request?')) return;

    try {
      await dispatch(cancelKeyRequestAsync(request.id)).unwrap();
      dispatch(fetchKeyRequests());
    } catch (error) {
      console.error('Failed to cancel request:', error);
      alert('Failed to cancel request. Please try again.');
    }
  };

  // Count stats
  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const approvedCount = requests.filter(r => r.status === 'approved').length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">Key Requests</h1>
          <p className="mt-1 text-sm text-secondary-600 dark:text-secondary-400">
            {isKeyRequester
              ? 'Request keys for new employees'
              : 'Review and manage key requests'}
            {pendingCount > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 text-xs font-medium rounded-full">
                {pendingCount} pending
              </span>
            )}
            {canApprove && approvedCount > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 text-xs font-medium rounded-full">
                {approvedCount} ready to fulfill
              </span>
            )}
          </p>
        </div>
        <Button onClick={handleOpenRequestModal} disabled={loading}>
          <PlusIcon className="w-5 h-5 mr-2" />
          New Request
        </Button>
      </div>

      {/* Search and Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-400" />
          <input
            type="text"
            placeholder="Search by person, requester, or notes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        <div className="flex items-center gap-2">
          <FunnelIcon className="w-5 h-5 text-secondary-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="px-3 py-2 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="fulfilled">Fulfilled</option>
          </select>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
          <span className="ml-2 text-secondary-600 dark:text-secondary-400">Loading...</span>
        </div>
      )}

      {/* Requests List */}
      {!loading && filteredRequests.length === 0 ? (
        <Card>
          <div className="py-12 text-center">
            <ClipboardDocumentListIcon className="w-12 h-12 mx-auto text-secondary-400" />
            <h3 className="mt-2 text-sm font-medium text-secondary-900 dark:text-secondary-100">
              {statusFilter !== 'all' || debouncedSearch ? 'No matching requests' : 'No key requests'}
            </h3>
            <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
              {statusFilter !== 'all' || debouncedSearch
                ? 'Try adjusting your search or filter criteria.'
                : 'Create a new request to get started.'}
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredRequests.map((request) => {
            const { keyType, location, person, requester, reviewer } = getRequestDetails(request);
            const locationColor = location?.color || '#6B7280';
            const canCancel = request.status === 'pending' && 
              (request.requestedBy === currentUser?.id || isAdmin);

            return (
              <Card key={request.id}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  {/* Main Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-secondary-900 dark:text-secondary-100">
                        {keyType?.name || 'Unknown Key'}
                      </span>
                      {request.quantity > 1 && (
                        <span className="text-secondary-500 dark:text-secondary-400">x{request.quantity}</span>
                      )}
                      {getStatusBadge(request.status)}
                    </div>
                    
                    <div className="mt-1 flex items-center gap-2 flex-wrap text-sm text-secondary-600 dark:text-secondary-400">
                      <span className="flex items-center gap-1">
                        For: <span className="font-medium text-secondary-900 dark:text-secondary-100">{person?.name || 'Unknown'}</span>
                      </span>
                      <span className="text-secondary-300 dark:text-secondary-600">|</span>
                      <span 
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full"
                        style={{ 
                          backgroundColor: `${locationColor}15`,
                          color: locationColor
                        }}
                      >
                        <span 
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: locationColor }}
                        />
                        {location?.name || 'Unknown Location'}
                      </span>
                    </div>

                    <div className="mt-2 text-xs text-secondary-500 dark:text-secondary-400">
                      Requested by {requester?.name || 'Unknown'} on {new Date(request.requestedAt).toLocaleDateString()}
                      {request.notes && (
                        <span className="ml-2 italic">- "{request.notes}"</span>
                      )}
                    </div>

                    {request.reviewedAt && reviewer && (
                      <div className="mt-1 text-xs text-secondary-500 dark:text-secondary-400">
                        {request.status === 'approved' ? 'Approved' : request.status === 'rejected' ? 'Rejected' : 'Reviewed'} by {reviewer.name} on {new Date(request.reviewedAt).toLocaleDateString()}
                        {request.reviewNotes && (
                          <span className="ml-2 italic">- "{request.reviewNotes}"</span>
                        )}
                      </div>
                    )}

                    {request.fulfilledAt && (
                      <div className="mt-1 text-xs text-green-600 dark:text-green-400">
                        Key assigned on {new Date(request.fulfilledAt).toLocaleDateString()}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {request.status === 'pending' && canApprove && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => handleOpenReviewModal(request, 'approve')}
                        >
                          <CheckCircleIcon className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleOpenReviewModal(request, 'reject')}
                        >
                          <XCircleIcon className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </>
                    )}

                    {request.status === 'approved' && canApprove && (
                      <Button
                        size="sm"
                        onClick={() => handleOpenFulfillModal(request)}
                      >
                        <KeyIcon className="w-4 h-4 mr-1" />
                        Fulfill & Assign Key
                      </Button>
                    )}

                    {canCancel && (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleCancelRequest(request)}
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Request Modal */}
      <Modal
        isOpen={isRequestModalOpen}
        onClose={handleCloseRequestModal}
        title="Request a Key"
        footer={
          <>
            <Button variant="secondary" onClick={handleCloseRequestModal} disabled={isSaving}>
              Cancel
            </Button>
            <Button 
              onClick={requestForm.handleSubmit(onSubmitRequest)} 
              disabled={isSaving || !selectedPerson || !selectedLocationId || !requestForm.watch('keyTypeId')}
            >
              {isSaving ? 'Submitting...' : 'Submit Request'}
            </Button>
          </>
        }
      >
        <form className="space-y-4">
          {/* Person Selection */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Request Key For *
            </label>
            {selectedPerson ? (
              <div className="flex items-center justify-between p-3 bg-secondary-50 dark:bg-secondary-700 rounded-lg">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-secondary-900 dark:text-secondary-100">{selectedPerson.name}</p>
                    {selectedPerson.source === 'azuread' && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300">
                        New from Azure AD
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-secondary-500 dark:text-secondary-400">{selectedPerson.email}</p>
                  {selectedPerson.source === 'azuread' && (
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                      This person will be added to the system when the request is submitted.
                    </p>
                  )}
                </div>
                <Button size="sm" variant="secondary" onClick={() => setSelectedPerson(null)}>
                  Change
                </Button>
              </div>
            ) : (
              <PersonSearch
                onChange={(person) => setSelectedPerson(person as PersonSearchResult | null)}
                placeholder="Search for a person..."
              />
            )}
          </div>

          {/* Location Selection */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Location *
            </label>
            <select
              value={selectedLocationId}
              onChange={(e) => setSelectedLocationId(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={isSaving}
            >
              <option value="">Select a location</option>
              {locations.map((loc: Location) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name}
                </option>
              ))}
            </select>
          </div>

          {/* Key Type Selection */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Key Type *
            </label>
            <select
              {...requestForm.register('keyTypeId', { required: true })}
              className="w-full px-4 py-2 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={isSaving}
            >
              <option value="">Select a key type</option>
              {keyTypes.map((keyType: KeyType) => {
                // Find inventory for this key type at selected location (if any)
                const invItem = selectedLocationId 
                  ? inventory.find((i: KeyInventoryItem) => i.keyTypeId === keyType.id && i.locationId === selectedLocationId)
                  : null;
                const availableQty = invItem?.quantity ?? 0;
                return (
                  <option key={keyType.id} value={keyType.id}>
                    {keyType.name} {selectedLocationId ? `(${availableQty} available)` : ''}
                  </option>
                );
              })}
            </select>
          </div>

          {/* Quantity */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Quantity
            </label>
            <input
              type="number"
              min="1"
              {...requestForm.register('quantity', { required: true, min: 1 })}
              className="w-full px-4 py-2 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={isSaving}
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Notes (optional)
            </label>
            <textarea
              {...requestForm.register('notes')}
              className="w-full px-4 py-2 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="e.g., Start date, department, reason for request..."
              rows={3}
              disabled={isSaving}
            />
          </div>
        </form>
      </Modal>

      {/* Review Modal (Approve/Reject) */}
      <Modal
        isOpen={isReviewModalOpen}
        onClose={handleCloseReviewModal}
        title={reviewAction === 'approve' ? 'Approve Request' : 'Reject Request'}
        footer={
          <>
            <Button variant="secondary" onClick={handleCloseReviewModal} disabled={isSaving}>
              Cancel
            </Button>
            <Button 
              variant={reviewAction === 'approve' ? 'primary' : 'danger'}
              onClick={handleReview} 
              disabled={isSaving}
            >
              {isSaving ? 'Processing...' : reviewAction === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </>
        }
      >
        {selectedRequest && (() => {
          const { keyType, location, person, requester } = getRequestDetails(selectedRequest);
          const locationColor = location?.color || '#6B7280';
          
          return (
            <div className="space-y-4">
              <div 
                className="p-4 rounded-lg border-l-4"
                style={{ 
                  backgroundColor: `${locationColor}10`,
                  borderLeftColor: locationColor
                }}
              >
                <p className="font-medium text-secondary-900 dark:text-secondary-100">
                  {keyType?.name || 'Unknown Key'} x{selectedRequest.quantity}
                </p>
                <p className="text-sm text-secondary-600 dark:text-secondary-400">
                  For: {person?.name || 'Unknown'}
                </p>
                <p className="text-sm text-secondary-600 dark:text-secondary-400">
                  Location: {location?.name || 'Unknown'}
                </p>
                <p className="text-sm text-secondary-600 dark:text-secondary-400">
                  Requested by: {requester?.name || 'Unknown'}
                </p>
                {selectedRequest.notes && (
                  <p className="mt-2 text-sm italic text-secondary-500 dark:text-secondary-400">
                    "{selectedRequest.notes}"
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  {reviewAction === 'approve' ? 'Approval Notes (optional)' : 'Rejection Reason'}
                </label>
                <textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder={reviewAction === 'approve' 
                    ? 'e.g., Key will be ready Monday' 
                    : 'e.g., Insufficient justification, please provide more details'}
                  rows={3}
                  disabled={isSaving}
                />
              </div>

              {reviewAction === 'approve' && (
                <p className="text-sm text-secondary-500 dark:text-secondary-400">
                  After approval, the key will still need to be physically assigned (fulfilled) before the request is complete.
                </p>
              )}
            </div>
          );
        })()}
      </Modal>

      {/* Fulfill Modal */}
      <Modal
        isOpen={isFulfillModalOpen}
        onClose={handleCloseFulfillModal}
        title="Fulfill Request & Assign Key"
        footer={
          <>
            <Button variant="secondary" onClick={handleCloseFulfillModal} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleFulfill} disabled={isSaving}>
              {isSaving ? 'Assigning...' : 'Assign Key'}
            </Button>
          </>
        }
      >
        {selectedRequest && (() => {
          const { keyType, location, person, invItem } = getRequestDetails(selectedRequest);
          const locationColor = location?.color || '#6B7280';
          const availableQty = invItem?.quantity || 0;
          const hasEnoughInventory = availableQty >= selectedRequest.quantity;
          
          return (
            <div className="space-y-4">
              <div 
                className="p-4 rounded-lg border-l-4"
                style={{ 
                  backgroundColor: `${locationColor}10`,
                  borderLeftColor: locationColor
                }}
              >
                <p className="font-medium text-secondary-900 dark:text-secondary-100">
                  {keyType?.name || 'Unknown Key'} x{selectedRequest.quantity}
                </p>
                <p className="text-sm text-secondary-600 dark:text-secondary-400">
                  For: {person?.name || 'Unknown'}
                </p>
                <p className="text-sm text-secondary-600 dark:text-secondary-400">
                  Location: {location?.name || 'Unknown'}
                </p>
                <p className={`text-sm ${hasEnoughInventory ? 'text-secondary-600 dark:text-secondary-400' : 'text-red-600 dark:text-red-400'}`}>
                  Available in inventory: {availableQty}
                </p>
              </div>

              {!hasEnoughInventory && (
                <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-700 dark:text-red-300">
                    Warning: There are only {availableQty} keys available but {selectedRequest.quantity} requested. 
                    Fulfilling this request will result in negative inventory.
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  Assignment Notes (optional)
                </label>
                <textarea
                  value={fulfillNotes}
                  onChange={(e) => setFulfillNotes(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., Key serial number, pickup location..."
                  rows={2}
                  disabled={isSaving}
                />
              </div>

              <p className="text-sm text-secondary-500 dark:text-secondary-400">
                This will create a key assignment and notify the requester that the key is ready.
              </p>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
}
